/**
 * Tripnovac Visa — Visa Detail Page
 * Full visa type details with requirements, fees, processing stats,
 * application channels, and embassy information.
 *
 * @company Alpha Libra
 * @domain tripnovac-visa.com
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Button, Chip, Paper, Skeleton, Alert, Divider,
  List, ListItem, ListItemIcon, ListItemText,
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Cancel, ExpandMore,
  AttachMoney, Schedule, Description, Assignment,
  Gavel, FlightTakeoff, Language, LocationOn,
  VerifiedUser, Info, Work, School,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import type { VisaTypeFull } from '../../types/visa';

const VisaDetailPage: React.FC = () => {
  const { countryCode, visaId } = useParams<{ countryCode: string; visaId: string }>();
  const navigate = useNavigate();

  const [visa, setVisa] = useState<VisaTypeFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVisa = useCallback(async () => {
    if (!visaId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.visa.full(visaId));
      if (!res.ok) throw new Error('Visa type not found');
      const data = await res.json();
      setVisa(data.data || data);
    } catch (err: any) {
      setError(err.message || 'Failed to load visa details');
    } finally {
      setLoading(false);
    }
  }, [visaId]);

  useEffect(() => { fetchVisa(); }, [fetchVisa]);

  const country = visa?.Country as any;
  const iso2 = countryCode || country?.iso2 || '';

  /* =============== Loading =============== */
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#030B1B' }}>
        <Skeleton variant="rectangular" height={200} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.06)' }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)' }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.06)' }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  /* =============== Error =============== */
  if (error || !visa) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error || 'Visa type not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(iso2 ? `/countries/${iso2}` : '/countries')}>
          Back to Country
        </Button>
      </Container>
    );
  }

  const requirements = visa.Requirement || [];
  const fees = visa.VisaFee || [];
  const processingStats = visa.ProcessingStat || [];
  const channels = visa.ApplicationChannel || [];
  const docs = visa.DocumentRequirement || [];
  const policies = visa.Policy || [];
  const legal = visa.LegalReference || [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _eligibility = visa.VisaEligibility || [];
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _guides = visa.ApplicationGuide || [];
  const consulates = (visa as any).Consulate || [];

  const flagEmoji = country?.flagEmoji || (iso2 ? String.fromCodePoint(...Array.from(iso2.toUpperCase()).map((c) => 127397 + (c as string).charCodeAt(0))) : '');

  /* =============== Main Render =============== */
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#030B1B' }}>
      {/* HERO */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 180,
          background: 'linear-gradient(135deg, #030B1B 0%, #0c1a3a 50%, #0f2b46 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          pb: 5,
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(iso2 ? `/countries/${iso2}` : '/countries')}
            sx={{ color: 'rgba(255,255,255,0.9)', mb: 1.5, textTransform: 'none' }}
          >
            {country?.name || 'Back to Country'}
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h3" component="span" sx={{ fontSize: 48 }}>{flagEmoji}</Typography>
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {visa.name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.85, mt: 0.5 }}>
                {country?.name || ''} — {visa.category ? visa.category.charAt(0).toUpperCase() + visa.category.slice(1) : ''} Visa
              </Typography>
            </Box>
          </Box>

          {/* Quick chips */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            {visa.isActive !== false && (
              <Chip label="Active" size="small" sx={{ bgcolor: 'rgba(46,125,50,0.7)', color: '#fff', fontWeight: 600 }} />
            )}
            {visa.online && (
              <Chip label="Online Application" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
            )}
            {visa.multipleEntry && (
              <Chip label="Multiple Entry" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
            )}
            {visa.allowsWork && (
              <Chip label="Work Permitted" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
            )}
            {visa.allowsStudy && (
              <Chip label="Study Permitted" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600 }} />
            )}
          </Box>
        </Container>
      </Box>

      {/* CONTENT */}
      <Container maxWidth="lg" sx={{ py: 4, mt: -4 }}>
        <Grid container spacing={4}>

          {/* LEFT COLUMN */}
          <Grid item xs={12} md={8}>

            {/* Overview */}
            <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                <Info sx={{ verticalAlign: 'middle', mr: 1, color: '#0EA5E9' }} />
                Overview
              </Typography>
              {visa.description && (
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: 'rgba(255,255,255,0.55)' }}>
                  {visa.description}
                </Typography>
              )}
              <Grid container spacing={2}>
                {[
                  { label: 'Validity', value: visa.validityDays ? `${visa.validityDays} days` : null, icon: <Schedule fontSize="small" /> },
                  { label: 'Max Stay', value: visa.stayDays ? `${visa.stayDays} days` : null, icon: <FlightTakeoff fontSize="small" /> },
                  { label: 'Processing', value: visa.processingAvgDays ? `${visa.processingAvgDays} days` : null, icon: <Schedule fontSize="small" /> },
                  { label: 'Fee', value: visa.feeAmount ? `${visa.feeCurrency || 'USD'} ${visa.feeAmount}` : null, icon: <AttachMoney fontSize="small" /> },
                  { label: 'Entry Type', value: visa.entryType || null, icon: <FlightTakeoff fontSize="small" /> },
                  { label: 'Category', value: visa.category || null, icon: <Description fontSize="small" /> },
                ].filter(item => item.value).map((item) => (
                  <Grid item xs={6} sm={4} key={item.label}>
                    <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                      <Box sx={{ color: '#0EA5E9', mb: 0.5 }}>{item.icon}</Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>{item.label}</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>{item.value}</Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Requirements */}
            {requirements.length > 0 && (
              <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <Assignment sx={{ verticalAlign: 'middle', mr: 1, color: '#0EA5E9' }} />
                  Requirements
                </Typography>
                <List disablePadding>
                  {requirements.map((req: any, idx: number) => (
                    <ListItem key={req.id || idx} sx={{ px: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircle fontSize="small" color={req.optional ? 'disabled' : 'success'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={req.field || req.value?.description || JSON.stringify(req.value)}
                        secondary={req.notes || (req.optional ? 'Optional' : 'Required')}
                        primaryTypographyProps={{ fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}
                        secondaryTypographyProps={{ color: 'rgba(255,255,255,0.4)' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Document Requirements */}
            {docs.length > 0 && (
              <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <Description sx={{ verticalAlign: 'middle', mr: 1, color: '#0EA5E9' }} />
                  Required Documents
                </Typography>
                <List disablePadding>
                  {docs.map((doc: any, idx: number) => (
                    <ListItem key={doc.id || idx} sx={{ px: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {doc.optional ? <Cancel fontSize="small" color="disabled" /> : <CheckCircle fontSize="small" color="success" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.docType}
                        secondary={doc.description || (doc.optional ? 'Optional' : 'Required')}
                        primaryTypographyProps={{ fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}
                        secondaryTypographyProps={{ color: 'rgba(255,255,255,0.4)' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Fees */}
            {fees.length > 0 && (
              <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <AttachMoney sx={{ verticalAlign: 'middle', mr: 1, color: '#10B981' }} />
                  Fees
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.06)' }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.06)' }}>Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.06)' }}>Currency</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.06)' }}>Refundable</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fees.map((fee: any, idx: number) => (
                        <TableRow key={fee.id || idx}>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.55)', borderColor: 'rgba(255,255,255,0.06)' }}>{fee.feeType || 'Standard'}</TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.55)', borderColor: 'rgba(255,255,255,0.06)' }}>{fee.amount}</TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.55)', borderColor: 'rgba(255,255,255,0.06)' }}>{fee.currency}</TableCell>
                          <TableCell sx={{ color: 'rgba(255,255,255,0.55)', borderColor: 'rgba(255,255,255,0.06)' }}>{fee.refundable ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Processing Stats */}
            {processingStats.length > 0 && (
              <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <Schedule sx={{ verticalAlign: 'middle', mr: 1, color: '#0EA5E9' }} />
                  Processing Time
                </Typography>
                <Grid container spacing={2}>
                  {processingStats.map((ps: any, idx: number) => (
                    <React.Fragment key={ps.id || idx}>
                      {ps.avgDays && (
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                            <Typography variant="h4" fontWeight={700} sx={{ color: '#0EA5E9' }}>{ps.avgDays}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>Average Days</Typography>
                          </Card>
                        </Grid>
                      )}
                      {ps.minDays && (
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                            <Typography variant="h4" fontWeight={700} sx={{ color: '#10B981' }}>{ps.minDays}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>Min Days</Typography>
                          </Card>
                        </Grid>
                      )}
                      {ps.maxDays && (
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                            <Typography variant="h4" fontWeight={700} sx={{ color: '#F59E0B' }}>{ps.maxDays}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>Max Days</Typography>
                          </Card>
                        </Grid>
                      )}
                      {ps.expeditedAvgDays && (
                        <Grid item xs={6} sm={3}>
                          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                            <Typography variant="h4" fontWeight={700} sx={{ color: '#8B5CF6' }}>{ps.expeditedAvgDays}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>Expedited (days)</Typography>
                          </Card>
                        </Grid>
                      )}
                    </React.Fragment>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* Application Channels */}
            {channels.length > 0 && (
              <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <Language sx={{ verticalAlign: 'middle', mr: 1, color: '#0EA5E9' }} />
                  How to Apply
                </Typography>
                <Grid container spacing={2}>
                  {channels.map((ch: any, idx: number) => (
                    <Grid item xs={12} sm={6} key={ch.id || idx}>
                      <Card variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'white' }}>
                          {ch.type === 'online' ? 'Online Application' : ch.type === 'embassy' ? 'Embassy / Consulate' : ch.type}
                        </Typography>
                        {ch.provider && <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>{ch.provider}</Typography>}
                        {ch.appointmentRequired && (
                          <Chip label="Appointment Required" size="small" sx={{ mt: 1, bgcolor: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }} />
                        )}
                        {ch.url && (
                          <Button size="small" href={ch.url} target="_blank" sx={{ mt: 1, textTransform: 'none', color: '#0EA5E9' }}>
                            Visit Portal
                          </Button>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* Policies  */}
            {policies.length > 0 && (
              <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <Gavel sx={{ verticalAlign: 'middle', mr: 1, color: '#8B5CF6' }} />
                  Policies
                </Typography>
                {policies.map((pol: any, idx: number) => (
                  <Accordion key={pol.id || idx} disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 1, bgcolor: 'rgba(255,255,255,0.02)', backgroundImage: 'none' }}>
                    <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'rgba(255,255,255,0.5)' }} />}>
                      <Typography fontWeight={600} sx={{ color: 'rgba(255,255,255,0.7)' }}>{pol.title}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>{pol.text}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Paper>
            )}

            {/* Legal References */}
            {legal.length > 0 && (
              <Paper sx={{ p: 3.5, borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <Gavel sx={{ verticalAlign: 'middle', mr: 1, color: '#8B5CF6' }} />
                  Legal References
                </Typography>
                <List disablePadding>
                  {legal.map((ref: any, idx: number) => (
                    <ListItem key={ref.id || idx} sx={{ px: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Gavel fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={ref.title}
                        secondary={ref.citation || ref.notes || ''}
                        primaryTypographyProps={{ fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}
                        secondaryTypographyProps={{ color: 'rgba(255,255,255,0.4)' }}
                      />
                      {ref.url && (
                        <Button size="small" href={ref.url} target="_blank" sx={{ textTransform: 'none', color: '#0EA5E9' }}>
                          View
                        </Button>
                      )}
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </Grid>

          {/* RIGHT COLUMN — Quick Info + Embassies */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ borderRadius: 3, overflow: 'hidden', position: 'sticky', top: 90, mb: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
              {/* Header */}
              <Box sx={{ px: 3, py: 2.5, background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', color: '#fff' }}>
                <Typography variant="h6" fontWeight={700}>Quick Info</Typography>
              </Box>
              <CardContent sx={{ p: 3 }}>
                {/* Key details */}
                <List disablePadding dense>
                  {[
                    { icon: <AttachMoney fontSize="small" />, label: 'Fee', value: visa.feeAmount ? `${visa.feeCurrency || 'USD'} ${visa.feeAmount}` : 'Contact embassy' },
                    { icon: <Schedule fontSize="small" />, label: 'Processing', value: visa.processingAvgDays ? `${visa.processingAvgDays} business days` : 'Varies' },
                    { icon: <FlightTakeoff fontSize="small" />, label: 'Validity', value: visa.validityDays ? `${visa.validityDays} days` : 'Varies' },
                    { icon: <FlightTakeoff fontSize="small" />, label: 'Max Stay', value: visa.stayDays ? `${visa.stayDays} days` : 'Varies' },
                    { icon: <Description fontSize="small" />, label: 'Entry', value: visa.multipleEntry ? 'Multiple' : 'Single' },
                    { icon: <Language fontSize="small" />, label: 'Online Apply', value: visa.online ? 'Yes' : 'No' },
                  ].map((item) => (
                    <ListItem key={item.label} sx={{ px: 0, py: 0.75 }}>
                      <ListItemIcon sx={{ minWidth: 30, color: '#0EA5E9' }}>{item.icon}</ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>{item.label}</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>{item.value}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.06)' }} />

                {/* Feature badges */}
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'white' }}>Features</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {visa.online && <Chip label="Online Application" size="small" sx={{ bgcolor: 'rgba(14,165,233,0.15)', color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.3)' }} />}
                  {visa.biometrics && <Chip label="Biometrics Required" size="small" sx={{ bgcolor: 'rgba(139,92,246,0.15)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)' }} />}
                  {visa.multipleEntry && <Chip label="Multiple Entry" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)' }} />}
                  {visa.allowsWork && <Chip label="Work Permitted" size="small" icon={<Work sx={{ color: '#F59E0B !important' }} />} sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }} />}
                  {visa.allowsStudy && <Chip label="Study Permitted" size="small" icon={<School sx={{ color: '#0EA5E9 !important' }} />} sx={{ bgcolor: 'rgba(14,165,233,0.15)', color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.3)' }} />}
                  {visa.insuranceReq && <Chip label="Insurance Required" size="small" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }} />}
                  {visa.interviewReq && <Chip label="Interview Required" size="small" sx={{ bgcolor: 'rgba(139,92,246,0.15)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)' }} />}
                  {visa.invitationReq && <Chip label="Invitation Required" size="small" sx={{ bgcolor: 'rgba(139,92,246,0.15)', color: '#8B5CF6', border: '1px solid rgba(139,92,246,0.3)' }} />}
                  {visa.extensionsAllowed && <Chip label="Extensions Allowed" size="small" sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981', border: '1px solid rgba(16,185,129,0.3)' }} />}
                  {visa.familyJoiningAllowed && <Chip label="Family Joining" size="small" sx={{ bgcolor: 'rgba(14,165,233,0.15)', color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.3)' }} />}
                </Box>

                {/* Apply CTA */}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={() => navigate(`/applications/new?country=${iso2}&visa=${visaId}`)}
                  sx={{ mt: 3, textTransform: 'none', fontWeight: 700, py: 1.5, background: 'linear-gradient(135deg, #0EA5E9, #14B8A6)', '&:hover': { background: 'linear-gradient(135deg, #0EA5E9, #10B981)' } }}
                >
                  Start Application
                </Button>
              </CardContent>
            </Paper>

            {/* Embassies */}
            {consulates.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <LocationOn sx={{ verticalAlign: 'middle', mr: 1, color: '#0EA5E9' }} />
                  Embassies & Consulates
                </Typography>
                <List disablePadding dense>
                  {consulates.map((con: any) => (
                    <ListItem key={con.id} sx={{ px: 0, py: 1, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                        <LocationOn fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={con.name}
                        secondary={
                          <>
                            {con.city && <span>{con.city}<br /></span>}
                            {con.address && <span>{con.address}<br /></span>}
                            {con.phone && <span>Tel: {con.phone}<br /></span>}
                            {con.email && <span>{con.email}<br /></span>}
                            {con.website && (
                              <Button size="small" href={con.website} target="_blank" sx={{ textTransform: 'none', p: 0, color: '#0EA5E9' }}>
                                Website
                              </Button>
                            )}
                          </>
                        }
                        primaryTypographyProps={{ fontWeight: 600, variant: 'body2', color: 'rgba(255,255,255,0.7)' }}
                        secondaryTypographyProps={{ color: 'rgba(255,255,255,0.4)' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Additional info cards */}
            {(visa.extensionPolicy || visa.overstayConsequences || visa.transitRules) && (
              <Paper sx={{ p: 3, borderRadius: 3, mt: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                  <VerifiedUser sx={{ verticalAlign: 'middle', mr: 1, color: '#F59E0B' }} />
                  Important Notes
                </Typography>
                {visa.extensionPolicy && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'rgba(255,255,255,0.7)' }}>Extension Policy</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>{visa.extensionPolicy}</Typography>
                  </Box>
                )}
                {visa.overstayConsequences && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#F59E0B' }}>Overstay Consequences</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>{visa.overstayConsequences}</Typography>
                  </Box>
                )}
                {visa.transitRules && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'rgba(255,255,255,0.7)' }}>Transit Rules</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>{visa.transitRules}</Typography>
                  </Box>
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VisaDetailPage;
